<script lang="ts">
    import PanelHeader from "./panels/_components/PanelHeader.svelte";
    import { AppStore, Panels } from "$/stores/AppStore";
    import Card from "$/components/card/Card.svelte";
    import {
        ActiveTopic,
        ActiveTopicArchive,
        ActiveTopicStore, snippetArchiveDefaultLimit,
        TopicAccessLevel,
    } from "$/stores/extension/ActiveTopicStore";
    import { TokenStore } from "$/stores/TokenStore";
    import PanelLoadingAnimation from "$/components/panels/_components/PanelLoadingAnimation.svelte";
    import SnippetArchiveEmpty from "$/images/snippets-archive-empty-state.png";
    import { onDestroy } from "svelte";
    import SVGIcon from "$/components/SVGIcon.svelte";
    import { isMobile } from "$/utils/isMobile";
    import { debounce } from "$/utils/Utils";
    import SvgIcon from "$/components/SVGIcon.svelte";

    let initialLoading = false;
    let loadingMore = false;
    let lastLoadedTopicId = "";
    let searchInput = "";
    let searchStarted = false;
    let allSnippetsLoaded = false;

    $: searchInput, search();

    const loadArchivedSnippets = async (offset: number) => {
        let archivedSnippets = [];
        offset ? (loadingMore = true) : (initialLoading = true);
        if (searchStarted) {
            archivedSnippets = await ActiveTopicStore.loadArchivedSnippets(
                searchInput,
                offset,
            );
        } else {
            archivedSnippets = await ActiveTopicStore.loadArchivedSnippets(
                null,
                offset,
            );
        }
        offset ? (loadingMore = false) : (initialLoading = false);
        allSnippetsLoaded = archivedSnippets.length < snippetArchiveDefaultLimit;
    };

    const search = () => {
        if (!searchStarted && searchInput.length < 3) return;
        if (searchStarted && searchInput === "") {
            loadArchivedSnippets(0);
            searchStarted = false;
            return;
        }
        initialLoading = true;
        debounceSearch();
    };

    const debounceSearch = debounce(
        async () => {
            searchStarted = true;
            loadArchivedSnippets(0)
        },
        500,
        false,
    );

    const activeTopicSubscription = ActiveTopic.subscribe(async (topic) => {
        if (
            topic &&
            lastLoadedTopicId != topic.id &&
            $AppStore.panel === Panels.SNIPPET_ARCHIVE
        ) {
            lastLoadedTopicId = topic.id;
            await loadArchivedSnippets(0);
        }
    });

    const handleScroll = (event: Event) => {
        const target = event.target as HTMLElement;
        const { scrollTop, scrollHeight, clientHeight } = target;
        const bottomReached = scrollTop + clientHeight >= scrollHeight - 50;

        if (
            bottomReached &&
            !initialLoading &&
            !loadingMore &&
            !allSnippetsLoaded
        ) {
            loadArchivedSnippets($ActiveTopicArchive.length);
        }
    };

    onDestroy(() => {
        activeTopicSubscription();
    });
</script>

<div class="archived-snippets-panel">
    <PanelHeader title="Snippets Archive" on:close={AppStore.closePanels} />
    {#if searchStarted || $ActiveTopicArchive.length > 0}
        <div class="search-wrapper">
            <div class="icon-wrapper">
                <SVGIcon name="magnifying-glass" size={16} height={16} />
            </div>
            <input
                class="search-input"
                bind:value={searchInput}
                placeholder="Search archive"
                autofocus={!$isMobile}
            />
            {#if searchInput}
                <button class="button" on:click={() => (searchInput = "")}>
                    <span class="icon-wrapper">
                        <SVGIcon name="close" size={16} height={16} />
                    </span>
                </button>
            {/if}
        </div>
    {/if}
    {#if initialLoading}
        <PanelLoadingAnimation />
    {:else if $ActiveTopicArchive.length > 0}
        <div class="archived-snippets" on:scroll={handleScroll}>
            {#each $ActiveTopicArchive as snippet, index (snippet.id)}
                <Card
                    card={snippet}
                    editRole={TopicAccessLevel.VIEWER}
                    user={$TokenStore?.user}
                    topicId={$ActiveTopic.id}
                    openInNewTab={true}
                    deleteSnippet={ActiveTopicStore.deleteSnippet}
                    bgColor={"#F3F4F6"}
                />
            {/each}
            {#if loadingMore}
                <div class="loading-more-spinner">
                    <SvgIcon name="spinner" />
                </div>
            {/if}
        </div>
    {:else}
        <div class="snippet-archive-empty-img text-center">
            <img
                class="mb-3"
                src={SnippetArchiveEmpty}
                alt="Snippet Archive Empty"
            />
            <span class="text-gray-400">
                {searchInput ? "No results found" : "Snippet Archive Empty"}
            </span>
        </div>
    {/if}
</div>

<style>
    @import "../main.css";

    .archived-snippets-panel {
        @apply reset flex h-full w-full flex-col bg-gray-100 !important;
    }

    .archived-snippets {
        @apply flex flex-1 flex-col overflow-y-auto px-4 pb-4;
    }

    .snippet-archive-empty-img {
        @apply m-auto px-8;
    }

    .search-wrapper {
        @apply flex items-center gap-2 border-b border-b-gray-300 bg-gray-200 py-2 pl-4 pr-3.5 text-gray-600 !important;
    }

    .search-wrapper .icon-wrapper {
        @apply grid place-items-center !important;
    }

    .search-input {
        @apply reset w-full text-sm !important;
    }

    .loading-more-spinner {
        @apply mx-auto mt-4 size-6 origin-center animate-spin text-gray-400;
    }
</style>
